import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { workspacePath } from "../lib/workspaces";
import { apiGet } from "../lib/api";
import { Users, MapPin, TrendingUp, Calendar, ArrowUpRight } from "lucide-react";

interface UsageStats {
  name: string;
  plan: string;
  checkInsThisMonth: number;
  checkInsTotal: number;
  locationsCount: number;
  planLimits: {
    checkInsPerMonth: number | null;
    locations: number | null;
  };
  recentDays: { date: string; count: number }[];
}

export function Usage() {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<UsageStats>("/stats").then((res) => {
      if (res.success && res.data) setStats(res.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="p-6 text-stone-500">Loading...</div>;
  if (!stats) return <div className="p-6 text-stone-500">Failed to load usage data.</div>;

  const checkInLimit = stats.planLimits.checkInsPerMonth;
  const checkInPercent = checkInLimit ? Math.min((stats.checkInsThisMonth / checkInLimit) * 100, 100) : 0;

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4 sm:mb-6 font-display">Usage</h1>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Users size={20} />}
          label="Check-ins this month"
          value={stats.checkInsThisMonth}
          sublabel={checkInLimit ? `of ${checkInLimit.toLocaleString()} limit` : "Unlimited"}
          color="brand"
        />
        <StatCard
          icon={<TrendingUp size={20} />}
          label="Total check-ins"
          value={stats.checkInsTotal}
          sublabel="All time"
          color="green"
        />
        <StatCard
          icon={<MapPin size={20} />}
          label="Active locations"
          value={stats.locationsCount}
          sublabel={stats.planLimits.locations ? `of ${stats.planLimits.locations} limit` : "Unlimited"}
          color="purple"
        />
        <StatCard
          icon={<Calendar size={20} />}
          label="Plan"
          value={stats.plan}
          sublabel={stats.name}
          color="orange"
        />
      </div>

      {/* Usage bar */}
      {checkInLimit && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-stone-700">Monthly check-in usage</span>
            <span className="text-sm text-stone-500">
              {stats.checkInsThisMonth.toLocaleString()} / {checkInLimit.toLocaleString()}
            </span>
          </div>
          <div className="w-full h-3 bg-stone-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                checkInPercent >= 90 ? "bg-red-500" : checkInPercent >= 70 ? "bg-yellow-500" : "bg-brand-500"
              }`}
              style={{ width: `${checkInPercent}%` }}
            />
          </div>
          {checkInPercent >= 90 && (
            <p className="text-xs text-red-600 mt-2">
              You're approaching your monthly limit.{" "}
              <Link to={workspacePath("billing")} className="underline font-medium hover:text-red-700">
                Upgrade your plan
              </Link>
            </p>
          )}
        </div>
      )}

      {/* Upgrade card for free plan */}
      {stats.plan === "FREE" && (
        <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-xl p-6 mb-8 text-white">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold font-display mb-1">Need more check-ins or locations?</h2>
              <p className="text-sm text-stone-300 leading-relaxed max-w-lg">
                Upgrade to Enterprise for unlimited check-ins, unlimited locations, extended MyInfo fields, custom retention policies, and dedicated support.
              </p>
            </div>
            <Link
              to={workspacePath("billing")}
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-stone-900 text-sm font-semibold rounded-lg hover:bg-stone-100 transition-colors"
            >
              Upgrade
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}

      {/* Recent activity */}
      {stats.recentDays && stats.recentDays.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <h2 className="text-sm font-medium text-stone-700 mb-4">Daily check-ins (last 14 days)</h2>
          <div className="flex items-end gap-1 h-32">
            {stats.recentDays.map((day) => {
              const max = Math.max(...stats.recentDays.map((d) => d.count), 1);
              const height = Math.max((day.count / max) * 100, 4);
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-stone-400">{day.count || ""}</span>
                  <div
                    className="w-full bg-brand-100 rounded-sm hover:bg-brand-200 transition-colors"
                    style={{ height: `${height}%` }}
                    title={`${day.date}: ${day.count} check-ins`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-[10px] text-stone-400">
              {stats.recentDays[0]?.date}
            </span>
            <span className="text-[10px] text-stone-400">
              {stats.recentDays[stats.recentDays.length - 1]?.date}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  sublabel,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sublabel: string;
  color: "brand" | "green" | "purple" | "orange";
}) {
  const colors = {
    brand: "bg-brand-50 text-brand-500",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5">
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${colors[color]} mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-semibold text-stone-900 font-display">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-sm text-stone-500 mt-0.5">{label}</p>
      <p className="text-xs text-stone-400 mt-1">{sublabel}</p>
    </div>
  );
}
